import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { collection, doc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  CareProfile, CareVital, CareCheckIn, CareMedication, MedAdherence,
  CareAppointment, CareIncident, CareTransportRequest, CareNote,
  CareDocument, CareTeamContact
} from '@/types';

// Create context
const ElderCareContext = createContext<ElderCareContextValue | undefined>(undefined);

interface ElderCareProviderProps {
  children: React.ReactNode;
  userId: string | undefined;
}

interface ElderCareContextValue {
  careProfiles: CareProfile[];
  isLoaded: boolean;
  allVitals: Map<string, CareVital[]>;
  allCheckIns: Map<string, CareCheckIn[]>;
  allMedications: Map<string, CareMedication[]>;
  allAdherence: Map<string, MedAdherence[]>;
  allIncidents: Map<string, CareIncident[]>;
  allAppointments: Map<string, CareAppointment[]>;
  allTransportRequests: Map<string, CareTransportRequest[]>;
  allNotes: Map<string, CareNote[]>;
  allDocuments: Map<string, CareDocument[]>;
  allCareTeam: Map<string, CareTeamContact[]>;
  callElderCare: (action: string, data?: Record<string, unknown>) => Promise<unknown>;
  getTodayAdherenceSummary: (profileId: string) => { due: number; taken: number };
  getUpcomingAppointments: (profileId: string, days: number) => CareAppointment[];
  getMissedCheckIns: (profileId: string, days: number) => number;
}

// Helper functions
const getTodayString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

export function ElderCareProvider({ children, userId }: ElderCareProviderProps) {
  const [careProfiles, setCareProfiles] = useState<CareProfile[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Use refs to store collections (mutable state for efficient updates)
  const allVitalsRef = useRef<Map<string, CareVital[]>>(new Map());
  const allCheckInsRef = useRef<Map<string, CareCheckIn[]>>(new Map());
  const allMedicationsRef = useRef<Map<string, CareMedication[]>>(new Map());
  const allAdherenceRef = useRef<Map<string, MedAdherence[]>>(new Map());
  const allIncidentsRef = useRef<Map<string, CareIncident[]>>(new Map());
  const allAppointmentsRef = useRef<Map<string, CareAppointment[]>>(new Map());
  const allTransportRequestsRef = useRef<Map<string, CareTransportRequest[]>>(new Map());
  const allNotesRef = useRef<Map<string, CareNote[]>>(new Map());
  const allDocumentsRef = useRef<Map<string, CareDocument[]>>(new Map());
  const allCareTeamRef = useRef<Map<string, CareTeamContact[]>>(new Map());
  
  // Separate state to trigger re-renders when data updates
  const [, setSnapshotTrigger] = useState(0);
  
  const unsubs = useRef<(() => void)[]>([]);

  // Clear subscriptions
  const clearSubscriptions = () => {
    unsubs.current.forEach(sub => sub());
    unsubs.current = [];
  };

  // Helper to add a subscription and track it
  const addSubscription = (sub: () => void) => {
    unsubs.current.push(sub);
  };

  // Initialize data structures for a profile
  const initializeProfileData = (profileId: string) => {
    if (!allVitalsRef.current.has(profileId)) allVitalsRef.current.set(profileId, []);
    if (!allCheckInsRef.current.has(profileId)) allCheckInsRef.current.set(profileId, []);
    if (!allMedicationsRef.current.has(profileId)) allMedicationsRef.current.set(profileId, []);
    if (!allAdherenceRef.current.has(profileId)) allAdherenceRef.current.set(profileId, []);
    if (!allIncidentsRef.current.has(profileId)) allIncidentsRef.current.set(profileId, []);
    if (!allAppointmentsRef.current.has(profileId)) allAppointmentsRef.current.set(profileId, []);
    if (!allTransportRequestsRef.current.has(profileId)) allTransportRequestsRef.current.set(profileId, []);
    if (!allNotesRef.current.has(profileId)) allNotesRef.current.set(profileId, []);
    if (!allDocumentsRef.current.has(profileId)) allDocumentsRef.current.set(profileId, []);
    if (!allCareTeamRef.current.has(profileId)) allCareTeamRef.current.set(profileId, []);
  };

  // Set up listeners for a single profile's subcollections
  const setupProfileListeners = (profileId: string) => {
    // Create paths for each subcollection
    const subcollections = [
      { name: 'vitals', ref: allVitalsRef, type: 'CareVital' },
      { name: 'checkIns', ref: allCheckInsRef, type: 'CareCheckIn' },
      { name: 'medications', ref: allMedicationsRef, type: 'CareMedication' },
      { name: 'adherence', ref: allAdherenceRef, type: 'MedAdherence' },
      { name: 'appointments', ref: allAppointmentsRef, type: 'CareAppointment' },
      { name: 'incidents', ref: allIncidentsRef, type: 'CareIncident' },
      { name: 'transportRequests', ref: allTransportRequestsRef, type: 'CareTransportRequest' },
      { name: 'notes', ref: allNotesRef, type: 'CareNote' },
      { name: 'documents', ref: allDocumentsRef, type: 'CareDocument' },
      { name: 'careTeam', ref: allCareTeamRef, type: 'CareTeamContact' }
    ];

    subcollections.forEach(({ name, ref }) => {
      const colRef = collection(db, `careProfiles/${profileId}/${name}`);
      const q = query(colRef);
      const unsubscribe = onSnapshot(q, (snapshot) => {
        // Update ref data
        const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        ref.current.set(profileId, items as never[]);
        
        // Trigger re-render (use state update)
        setSnapshotTrigger(prev => prev + 1);
      });
      addSubscription(unsubscribe);
    });
  };

  useEffect(() => {
    clearSubscriptions();
    
    if (!userId) {
      // Reset to empty state
      setCareProfiles([]);
      allVitalsRef.current.clear();
      allCheckInsRef.current.clear();
      allMedicationsRef.current.clear();
      allAdherenceRef.current.clear();
      allIncidentsRef.current.clear();
      allAppointmentsRef.current.clear();
      allTransportRequestsRef.current.clear();
      allNotesRef.current.clear();
      allDocumentsRef.current.clear();
      allCareTeamRef.current.clear();
      setIsLoaded(true);
      return;
    }

    // Fetch profiles once — profile list rarely changes and doesn't need real-time streaming
    const profilesColRef = collection(db, 'careProfiles');
    const q = query(profilesColRef, where('memberUids', 'array-contains', userId));

    getDocs(q).then((snapshot) => {
      const profiles: CareProfile[] = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      })) as CareProfile[];

      setCareProfiles(profiles);
      setIsLoaded(true);

      // Only open subcollection listeners if the user actually has care profiles
      profiles.forEach(profile => {
        initializeProfileData(profile.id);
        setupProfileListeners(profile.id);
      });
    }).catch(() => {
      setIsLoaded(true);
    });

    return () => {
      clearSubscriptions();
    };
  }, [userId]);

  // Helper to call elder care Firebase function
  const callElderCare = async (action: string, data: Record<string, unknown> = {}) => {
    const fn = httpsCallable(getFunctions(), 'apiElderCare');
    const result = await fn({ action, ...data });
    return result.data;
  };

  // Helper: Get today's adherence summary for a profile
  const getTodayAdherenceSummary = (profileId: string) => {
    const today = getTodayString();
    const adherenceList = allAdherenceRef.current.get(profileId) || [];
    
    let due = 0;
    let taken = 0;

    adherenceList.forEach(adherence => {
      if (adherence.scheduledDate === today) {
        due++;
        if (adherence.status === 'taken') {
          taken++;
        }
      }
    });

    return { due, taken };
  };

  // Helper: Get upcoming appointments within a number of days
  const getUpcomingAppointments = (profileId: string, days: number) => {
    const appointments = allAppointmentsRef.current.get(profileId) || [];
    const now = new Date();
    const futureLimit = new Date(now.getTime() + days * 86400000);
    
    return appointments
      .filter(appointment => {
        if (appointment.status !== 'upcoming') return false;
        const appointmentDate = new Date(appointment.dateTime);
        return appointmentDate >= now && appointmentDate <= futureLimit;
      })
      .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
  };

  // Helper: Get count of missed check-ins over last N days
  const getMissedCheckIns = (profileId: string, days: number) => {
    const checkIns = allCheckInsRef.current.get(profileId) || [];
    const today = new Date();

    // Build a set of dates that have check-ins
    const checkInDates = new Set<string>();
    checkIns.forEach(checkIn => {
      const date = new Date(checkIn.loggedAt || checkIn.date);
      checkInDates.add(date.toISOString().split('T')[0]);
    });

    // Count missing check-ins for last N days (including today)
    let missedCount = 0;
    for (let i = 0; i < days; i++) {
      const date = new Date(today.getTime() - i * 86400000);
      const dateStr = date.toISOString().split('T')[0];
      if (!checkInDates.has(dateStr)) {
        missedCount++;
      }
    }

    return missedCount;
  };

  // Create context value
  const contextValue: ElderCareContextValue = {
    careProfiles,
    isLoaded,
    allVitals: allVitalsRef.current,
    allCheckIns: allCheckInsRef.current,
    allMedications: allMedicationsRef.current,
    allAdherence: allAdherenceRef.current,
    allIncidents: allIncidentsRef.current,
    allAppointments: allAppointmentsRef.current,
    allTransportRequests: allTransportRequestsRef.current,
    allNotes: allNotesRef.current,
    allDocuments: allDocumentsRef.current,
    allCareTeam: allCareTeamRef.current,
    callElderCare,
    getTodayAdherenceSummary,
    getUpcomingAppointments,
    getMissedCheckIns
  };

  return (
    <ElderCareContext.Provider value={contextValue}>
      {children}
    </ElderCareContext.Provider>
  );
}

export function useElderCare() {
  const context = useContext(ElderCareContext);
  if (!context) {
    throw new Error('useElderCare must be used within an ElderCareProvider');
  }
  return context;
}